import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import './PrizesModal.css'
import { SpinPrizePublicExtended, SpinPrizeSector } from '../../../../types/api'
import konfetiImage from '../../../../assets/images/konfeti.png'

interface PrizesModalProps {
  isOpen: boolean
  onClose: () => void
  wonPrizes: SpinPrizePublicExtended[]
  availablePrizes: SpinPrizeSector[]
}

type TabType = 'won' | 'available'

const PrizesModal = ({
  isOpen,
  onClose,
  wonPrizes,
  availablePrizes,
}: PrizesModalProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('won')

  // Обработчик закрытия по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="prizes-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="prizes-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <img src={konfetiImage} alt="" className="prizes-modal-konfeti" />
            <div className="prizes-modal-header">
              <h2 className="prizes-modal-title">Призы</h2>
              <button className="prizes-modal-close" onClick={onClose}>
                <X size={24} color="#FEFDFD" />
              </button>
            </div>

            <div className="prizes-modal-tabs">
              <button
                className={`prizes-tab ${activeTab === 'won' ? 'active' : ''}`}
                onClick={() => setActiveTab('won')}
              >
                <span>Вы выиграли</span>
              </button>
              <button
                className={`prizes-tab ${activeTab === 'available' ? 'active' : ''}`}
                onClick={() => setActiveTab('available')}
              >
                <span> Можно выиграть</span>
              </button>
            </div>

            <div className="prizes-modal-content">
              {activeTab === 'won' && (
                <>
                  {wonPrizes.length === 6 ? (
                    <div className="prizes-empty">
                      <p>У вас пока нет выигранных призов</p>
                      <p className="prizes-empty-subtitle">
                        Крутите колесо фортуны каждый день!
                      </p>
                    </div>
                  ) : (
                    <div className="prizes-list">
                      {wonPrizes.map(prize => (
                        <div
                          key={prize.spin_prize_id}
                          className="prize-card available"
                        >
                          <p className="prize-date">
                            {formatDate(prize.user_spin_at)}
                          </p>
                          <div className="prize-icon-container">
                            <img
                              src={prize.icon_url}
                              alt="Prize"
                              className="prize-icon"
                            />
                          </div>
                          <div className="prize-info">
                            <h3 className="prize-name">{prize.header_text}</h3>
                            <p className="prize-description">{prize.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'available' && (
                <>
                  {availablePrizes.length === 0 ? (
                    <div className="prizes-empty">
                      <p>Нет доступных призов</p>
                      <p className="prizes-empty-subtitle">
                        Акция временно недоступна
                      </p>
                    </div>
                  ) : (
                    <div className="prizes-list">
                      {availablePrizes.map(sector => (
                        <div
                          key={sector.spin_sector_id}
                          className="prize-card available"
                        >
                          <div className="prize-icon-container">
                            <img
                              src={sector.icon_url}
                              alt="Prize"
                              className="prize-icon"
                            />
                          </div>
                          <div className="prize-info">
                            <h3 className="prize-name">{sector.header_text}</h3>
                            <p className="prize-description">{sector.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default PrizesModal
